import React, { useState, useRef, useEffect } from 'react';

// Single source of truth for method metadata. Add a new payment type here and
// in METHOD_ORDER; nothing else needs to change.
const METHOD_CONFIG = {
    card: {
        id: 'card',
        name: 'Card',
        icon: 'fas fa-credit-card'
    },
    zelle: {
        id: 'zelle',
        name: 'Zelle',
        icon: 'fas fa-university'
    },
    venmo: {
        id: 'venmo',
        name: 'Venmo',
        icon: 'fas fa-mobile-alt'
    },
    cashapp: {
        id: 'cashapp',
        name: 'Cash App',
        icon: 'fas fa-dollar-sign'
    },
    paypal: {
        id: 'paypal',
        name: 'PayPal',
        icon: 'fab fa-paypal'
    },
    inperson_number: {
        id: 'inperson_number',
        name: 'Pay in Person',
        icon: 'fas fa-handshake'
    },
    free: {
        id: 'free',
        name: 'Complimentary',
        icon: 'fas fa-check-circle'
    }
};

// Display order in the dropdown, independent of the order the parent passes in.
const METHOD_ORDER = ['card', 'zelle', 'venmo', 'cashapp', 'paypal', 'inperson_number', 'free'];

const PaymentSelect = ({ selectedMethod, selectMethod, methods }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const available = Array.isArray(methods) ? methods : [];

        const updateMethods = METHOD_ORDER
            .filter((id) => available.includes(id))
            .map((id) => METHOD_CONFIG[id]);

        setPaymentMethods(updateMethods);

        // Set initial selectedOption if not already set
        if (!selectedMethod && updateMethods.length > 0) {
            selectMethod(updateMethods[0].id);
        }
    }, [methods]);

    useEffect(() => {
        // Update selectedOption when selectedMethod or paymentMethods change
        const option = paymentMethods.find(m => m.id === selectedMethod);
        setSelectedOption(option || paymentMethods[0] || null);
    }, [selectedMethod, paymentMethods]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (methodId) => {
        setIsOpen(false);
        selectMethod(methodId);
    };

    return (
        <form className="payment-select-wrapper" ref={dropdownRef} autoComplete="on">
            <label className="payment-label">Payment Method</label>
            <div className="dropdown-container">
                <div
                    className={`payment-dropdown ${isOpen ? 'open' : ''} ${selectedMethod === 'free' ? 'disabled' : ''}`}
                    onClick={() => selectedMethod !== 'free' && setIsOpen(!isOpen)}
                    style={selectedMethod === 'free' ? { opacity: 0.4, pointerEvents: 'none' } : {}}
                >
                    {selectedOption && (
                        <>
                            <div className="selected-option">
                                <i className={selectedOption.icon}></i>
                                <span>{selectedOption.name}</span>
                            </div>
                            <i className={`fas fa-chevron-down arrow ${isOpen ? 'rotated' : ''}`}></i>
                        </>
                    )}
                    {!selectedOption && (
                        <div className="placeholder">
                            Select payment method
                        </div>
                    )}
                </div>

                {isOpen && paymentMethods.length > 0 && (
                    <div className="dropdown-menu">
                        {paymentMethods.map((method) => (
                            <div
                                key={method.id}
                                className={`dropdown-option ${selectedMethod === method.id ? 'selected' : ''}`}
                                onClick={() => handleSelect(method.id)}
                            >
                                <i className={method.icon}></i>
                                <span>{method.name}</span>
                                {selectedMethod === method.id && (
                                    <i className="fas fa-check check-icon"></i>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style jsx>{`
                @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css');

                /* Monochrome checkout palette (Square-style):
                   #FFFFFF surface · #F6F6F6 raised · #D6D6D6 border · #000000 ink
                   #767676 secondary · #A3A3A3 tertiary */

                .payment-select-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 250px;
                    margin-top: 10px;
                    margin-bottom: 10px;
                }

                .payment-label {
                    display: block;
                    color: #767676;
                    font-size: 0.875rem;
                    font-weight: 500;
                    margin-bottom: 0.5rem;
                    letter-spacing: 0.025em;
                }

                .dropdown-container {
                    position: relative;
                    width: 100%;
                }

                .payment-dropdown {
                    background: #FFFFFF;
                    border: 1px solid #D6D6D6;
                    border-radius: 0.75rem;
                    padding: 0.75rem 1rem;
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    transition: all 0.2s ease;
                    min-height: 44px;
                }

                .payment-dropdown:hover {
                    background: #FAFAFA;
                    border-color: #B8B8B8;
                }

                .payment-dropdown.open {
                    background: #FFFFFF;
                    border-color: #000000;
                    box-shadow: 0 0 0 1px #000000;
                }

                .selected-option {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    color: #000000;
                    font-size: 0.95rem;
                    font-weight: 500;
                }

                .selected-option i {
                    color: #767676;
                    font-size: 1rem;
                    width: 1.25rem;
                }

                .placeholder {
                    color: #A3A3A3;
                    font-size: 0.95rem;
                }

                .arrow {
                    color: #767676;
                    font-size: 0.75rem;
                    transition: transform 0.2s ease;
                }

                .arrow.rotated {
                    transform: rotate(180deg);
                }

                .dropdown-menu {
                    position: absolute;
                    top: calc(100% + 0.5rem);
                    left: 0;
                    right: 0;
                    background: #FFFFFF;
                    border: 1px solid #E0E0E0;
                    border-radius: 0.75rem;
                    overflow: hidden;
                    z-index: 1000;
                    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
                    animation: slideDown 0.2s cubic-bezier(0.32, 0, 0.24, 1);
                    max-height: 320px;
                    overflow-y: auto;
                }

                .dropdown-option {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.875rem 1rem;
                    color: #000000;
                    font-size: 0.95rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    position: relative;
                }

                .dropdown-option i {
                    font-size: 1rem;
                    width: 1.25rem;
                    color: #767676;
                }

                .dropdown-option:hover {
                    background: #F2F2F2;
                    color: #000000;
                }

                .dropdown-option:hover i {
                    color: #000000;
                }

                .dropdown-option.selected {
                    background: #F6F6F6;
                    color: #000000;
                    font-weight: 600;
                }

                .dropdown-option.selected i {
                    color: #000000;
                }

                .check-icon {
                    margin-left: auto;
                    color: #000000;
                    font-size: 0.875rem;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-6px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    .dropdown-menu { animation: none; }
                    .arrow { transition: none; }
                }

                @media (max-width: 480px) {
                    .payment-select-wrapper {
                        max-width: 100%;
                    }

                    .payment-dropdown {
                        padding: 0.875rem 1rem;
                    }

                    .selected-option {
                        font-size: 1rem;
                    }
                }
            `}</style>
        </form>
    );
};

export default PaymentSelect;